import React from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Grid,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PersonIcon from "@mui/icons-material/Person";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DescriptionIcon from "@mui/icons-material/Description";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PhoneDisabledIcon from "@mui/icons-material/PhoneDisabled";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: 600, md: 700 },
  bgcolor: "#f6f9ff",
  borderRadius: "12px",
  boxShadow: 24,
  overflow: "hidden",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
};

const NotifyEmergencyContactModal = ({ open, handleClose, selectedRow }) => {
  if (!selectedRow) return null;

  const allUser = selectedRow?.allUser || {};
  const userData = selectedRow?.userData || {};
  
  // 1. Extract Emergency Contacts
  const emergencyContacts = allUser?.EmergencyContacts
    ? Array.from(
        new Map(
          Object.values(allUser.EmergencyContacts).map((contact) => [contact.phoneNumber, contact])
        ).values()
      )
    : [];
  const contact = emergencyContacts.length > 0 ? emergencyContacts[0] : null;
  const contactName = contact?.Name || contact?.name || selectedRow?.contactName || "Emergency Contact";
  const contactRelation = contact?.relation || contact?.relationship || "Contact";
  const contactPhone = contact?.phoneNumber || contact?.phone || selectedRow?.contactPhone || "N/A";

  // 2. Extract Location
  const lat = allUser?.currentLat || allUser?.LiveLatitude || allUser?.Latitude || userData?.Latitude || "";
  const lng = allUser?.currentLong || allUser?.LiveLongitude || allUser?.Longitude || userData?.Longitude || "";
  const address = selectedRow?.pickupLocation || selectedRow?.address || allUser?.address || (selectedRow?.state ? `${selectedRow.state}, Nigeria` : "Location Recorded");
  
  // 3. Extract time/date
  const timestamp = allUser?.currentTimeStamp || allUser?.updatedAt || selectedRow?.dateOfRegistration;
  const lastUpdate = timestamp ? new Date(timestamp).toLocaleString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, month: "short", day: "numeric", year: "numeric" }) : "9:07 PM, 24 Sept 2026";
  const confirmTime = new Date().toLocaleString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, month: "short", day: "numeric", year: "numeric" });

  const handleWhatsApp = () => {
    const cleanPhone = contactPhone.replace(/\\D/g, "");
    window.open(`https://wa.me/${cleanPhone}`, "_blank");
  };

  const handleGoogleMap = () => {
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank");
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        {/* Header */}
        <Box
          sx={{
            bgcolor: "#0b2342",
            color: "#fff",
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Notify Emergency Contact
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: "#fff" }}>
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2, overflowY: "auto" }}>
          {/* Top User Info */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <Box
              sx={{
                flex: 1,
                bgcolor: "#fff",
                borderRadius: "8px",
                p: 2,
                display: "flex",
                alignItems: "center",
                gap: 2,
                border: "1px solid #e3e8ef",
                minWidth: 250,
              }}
            >
              {selectedRow?.userImage ? (
                <Box
                  component="img"
                  src={selectedRow.userImage}
                  alt={selectedRow?.name}
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: "50%",
                    objectFit: "cover"
                  }}
                />
              ) : (
                <Box
                  sx={{
                    bgcolor: "#f0f4f8",
                    borderRadius: "50%",
                    width: 50,
                    height: 50,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <PersonIcon sx={{ color: "#0b2342", fontSize: 30 }} />
                </Box>
              )}
              <Box>
                <Typography fontWeight={800} fontSize="1.1rem" color="#0b2342">
                  {selectedRow?.name || "Escort User"}
                </Typography>
                <Typography fontSize="0.85rem" color="#5b6b8f">
                  {selectedRow?.id ? `ID #${selectedRow.id.toString().slice(-4)}` : "Trip"} | {selectedRow?.priority === "Critical" ? "Critical Alert" : "Active Escort"}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                flex: 1,
                bgcolor: "#ffeeee",
                borderRadius: "8px",
                p: 2,
                display: "flex",
                alignItems: "center",
                gap: 2,
                border: "1px solid #ffcccc",
                minWidth: 250,
              }}
            >
              <PhoneDisabledIcon sx={{ color: "#d32f2f", fontSize: 32 }} />
              <Box>
                <Typography fontWeight={700} color="#d32f2f" fontSize="0.95rem">
                  Phone/GPS Connection Lost
                </Typography>
                <Typography fontSize="0.8rem" color="#5b6b8f">
                  Manually confirmed by: Admin Operator<br/>
                  {confirmTime}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Emergency Contact */}
          <Box sx={{ bgcolor: "#fff", borderRadius: "8px", border: "1px solid #e3e8ef", p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <PeopleAltIcon sx={{ color: "#0b2342" }} />
              <Typography fontWeight={700} color="#0b2342">
                Emergency Contact
              </Typography>
            </Box>
            <Grid container spacing={1} sx={{ pl: 4 }}>
              <Grid item xs={4}>
                <Typography fontSize="0.85rem" color="#5b6b8f">Name:</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography fontSize="0.85rem" fontWeight={700} color="#0b2342">{contactName}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography fontSize="0.85rem" color="#5b6b8f">Relationship:</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography fontSize="0.85rem" fontWeight={700} color="#0b2342">{contactRelation}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography fontSize="0.85rem" color="#5b6b8f">Phone (WhatsApp):</Typography>
              </Grid>
              <Grid item xs={8} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography fontSize="0.85rem" fontWeight={700} color="#0b2342">{contactPhone}</Typography>
                <WhatsAppIcon sx={{ color: "#25D366", fontSize: 18 }} />
              </Grid>
            </Grid>
          </Box>

          {/* Last Known Location */}
          <Box sx={{ bgcolor: "#fff", borderRadius: "8px", border: "1px solid #e3e8ef", p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <LocationOnIcon sx={{ color: "#0b2342", mt: 0.5 }} />
              <Box>
                <Typography fontWeight={700} color="#0b2342">
                  Last Known Location
                </Typography>
                <Typography fontSize="0.85rem" color="#0b2342" mt={0.5}>
                  {address}
                </Typography>
                <Typography fontSize="0.8rem" color="#5b6b8f">
                  {lat}, {lng} | {lastUpdate}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              startIcon={<LocationOnIcon />}
              onClick={handleGoogleMap}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: "#1976d2",
                borderColor: "#1976d2",
                borderRadius: "8px",
              }}
            >
              View on Google Map
            </Button>
          </Box>

          {/* Security Alert */}
          <Box sx={{ bgcolor: "#fff", borderRadius: "8px", border: "1px solid #e3e8ef", p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <DescriptionIcon sx={{ color: "#0b2342" }} />
              <Typography fontWeight={700} color="#0b2342">
                Security Alert for Emergency Contact
              </Typography>
            </Box>
            <Box sx={{ bgcolor: "#f0f4f8", borderRadius: "6px", p: 2 }}>
              <Typography fontSize="0.85rem" fontWeight={800} color="#0b2342" mb={1}>
                SECURITY ALERT — GPS/PHONE CONNECTION LOST
              </Typography>
              <Typography fontSize="0.85rem" color="#4a5568" mb={1.5}>
                City Protection and Investment Limited (CPIL), the provider of {selectedRow?.name || "the user"}'s active Virtual Escort service, has lost GPS/location contact with them during the trip.<br/>
                The phone has been confirmed switched off, and the live location is no longer available.
              </Typography>
              <Typography fontSize="0.85rem" color="#0b2342" fontWeight={600} mb={1.5}>
                Last known location: <span style={{ fontWeight: 400 }}>{address} {lat && lng ? `(${lat}, ${lng})` : ""}</span><br/>
                Last location update: <span style={{ fontWeight: 400 }}>{lastUpdate}</span>
              </Typography>
              <Typography fontSize="0.85rem" color="#4a5568" mb={1.5}>
                You are receiving this alert because {selectedRow?.name || "the user"} registered you as an emergency contact.<br/>
                CPIL is alerting you and will attempt to contact available security agents close to the last known location.<br/>
                Please note that the availability of security agents cannot always be guaranteed.
              </Typography>
              <Typography fontSize="0.85rem" color="#4a5568" mb={1.5}>
                Please try to contact {selectedRow?.name || "the user"} immediately to confirm safety.
              </Typography>
              <Typography fontSize="0.85rem" color="#4a5568">
                For more information or assistance, WhatsApp CPIL: <strong>08056669464</strong>
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            fullWidth
            onClick={handleWhatsApp}
            sx={{
              bgcolor: "#128C7E",
              color: "#fff",
              py: 1.5,
              fontSize: "1rem",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "8px",
              display: "flex",
              justifyContent: "space-between",
              "&:hover": { bgcolor: "#075E54" },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, margin: "0 auto" }}>
              <WhatsAppIcon sx={{ fontSize: 24 }} />
              Send via WhatsApp to {contactPhone}
            </Box>
            <Box component="span" sx={{ fontSize: 24, position: "absolute", right: 16 }}>→</Box>
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default NotifyEmergencyContactModal;
